import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, MapPin, Phone, Mail, Download, Lock, Heart, Star, ShieldCheck, FolderArchive } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/seo/SEO";
import api, { API, formatApiErrorDetail } from "@/lib/api";

export default function CandidateDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [favorited, setFavorited] = useState(false);

  const loadCandidate = () => api.get(`/profiles/candidate/${id}`).then(({ data }) => setCandidate(data));

  useEffect(() => { loadCandidate(); }, [id]);

  const handleToggleFavorite = async () => {
    try {
      const { data } = await api.post("/favorites/toggle", { target_type: "candidate", target_id: id });
      setFavorited(data.favorited);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    }
  };

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      await api.post(`/profiles/candidate/${id}/unlock`);
      toast.success(t("application.contactRevealed"));
      await loadCandidate();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(t("credits.noCreditsMessage"));
        navigate("/dashboard/producer/credits");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
      }
    } finally {
      setUnlocking(false);
    }
  };

  if (!candidate) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#4ba3b7]" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="candidate-detail-page">
      <SEO title={`${candidate.first_name} - Candidat saisonnier vitivinicole | JobVinum`}
           description={`Profil de ${candidate.first_name}, candidat saisonnier pour la filière vitivinicole. Consultez son expérience et contactez-le sur JobVinum.`} path={`/candidates/${id}`} />
      <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6 sm:p-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-16 w-16 rounded-full bg-[#FAFAFA] border border-black/10 overflow-hidden flex items-center justify-center shrink-0">
            {candidate.photo_path ? (
              <img src={`${API}/profiles/files/${candidate.photo_path}`} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-xl font-bold text-[#4ba3b7]">{candidate.first_name?.[0]}</span>
            )}
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold">{candidate.first_name} {candidate.last_name}</h1>
            {candidate.nationality && <p className="text-sm text-[#2B2B2B]/50">{t(`lookups.nationalities.${candidate.nationality}`)}</p>}
            {candidate.rating?.average && (
              <div className="flex items-center gap-1 text-sm mt-1" data-testid="candidate-detail-rating">
                <Star className="h-4 w-4 fill-[#dde165] text-[#dde165]" />
                <span className="font-semibold">{candidate.rating.average}</span>
                <span className="text-[#2B2B2B]/40">({candidate.rating.count})</span>
              </div>
            )}
          </div>
          {user?.role === "producer" && (
            <button onClick={handleToggleFavorite} className="ml-auto" data-testid="candidate-detail-favorite-toggle-button" aria-label="favorite">
              <Heart className={`h-6 w-6 ${favorited ? "fill-[#FF3B30] text-[#FF3B30]" : "text-[#2B2B2B]/30"}`} />
            </button>
          )}
        </div>

        {candidate.description && <p className="text-sm text-[#2B2B2B]/70 mb-6">{candidate.description}</p>}

        {candidate.is_tesa_ready && (
          <Badge className="mb-6 bg-[#34C759]/10 text-[#34C759] border-0 rounded-full gap-1.5 py-1.5 px-3" data-testid="candidate-detail-tesa-badge">
            <ShieldCheck className="h-4 w-4" /> {t("candidateProfile.tesaReadyBadge")}
          </Badge>
        )}

        {candidate.skills?.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-wide text-[#2B2B2B]/50 mb-2">{t("candidateProfile.skillsSection")}</p>
            <div className="flex flex-wrap gap-2">
              {candidate.skills.map((s) => <Badge key={s} variant="outline" className="rounded-full">{t(`lookups.skills.${s}`)}</Badge>)}
            </div>
          </div>
        )}

        {candidate.countries_wanted?.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-[#2B2B2B]/70 mb-6">
            <MapPin className="h-4 w-4 text-[#4ba3b7]" />
            {candidate.countries_wanted.map((c) => t(`lookups.countries.${c}`)).join(", ")}
          </div>
        )}

        {candidate.mission_preferences?.length > 0 && (
          <div className="mb-6" data-testid="candidate-detail-mission-prefs">
            <p className="text-xs font-bold uppercase tracking-wide text-[#2B2B2B]/50 mb-2">{t("candidateProfile.missionPreferencesSection")}</p>
            <div className="space-y-2">
              {candidate.mission_preferences.map((pref, idx) => pref.region && (
                <div key={idx} className="flex items-center gap-2 text-sm text-[#2B2B2B]/70">
                  <MapPin className="h-4 w-4 text-[#4ba3b7] shrink-0" />
                  <span>
                    {t(`lookups.regions.${pref.region}`)}
                    {pref.date_start && pref.date_end && (
                      <> &middot; {new Date(pref.date_start).toLocaleDateString()} &rarr; {new Date(pref.date_end).toLocaleDateString()}</>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(candidate.worker_status || candidate.rate_type) && (
          <div className="flex flex-wrap gap-2 mb-6">
            {candidate.worker_status && (
              <Badge variant="outline" className="rounded-full border-[#4ba3b7]/30 text-[#4ba3b7]">
                {t(`lookups.workerStatuses.${candidate.worker_status}`)}
              </Badge>
            )}
            {candidate.rate_type && candidate.rate_amount != null && (
              <Badge variant="outline" className="rounded-full">
                {candidate.rate_amount}&euro; / {t(`lookups.rateTypes.${candidate.rate_type}`)}
              </Badge>
            )}
          </div>
        )}

        <div className="border-t border-black/5 pt-6">
          {candidate.unlocked ? (
            <div className="space-y-3" data-testid="candidate-unlocked-contact-section">
              {candidate.phone && <p className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-[#34C759]" /> {candidate.phone}</p>}
              {candidate.email && <p className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-[#34C759]" /> {candidate.email}</p>}
              {(candidate.documents || []).map((doc) => (
                <a key={doc.storage_path} href={`${API}/profiles/files/${doc.storage_path}`} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 text-sm text-[#4ba3b7] hover:underline" data-testid={`candidate-document-download-${doc.type}`}>
                  <Download className="h-4 w-4" /> {doc.filename}
                </a>
              ))}
              {(candidate.documents || []).length > 0 && (
                <a href={`${API}/profiles/candidate/${id}/documents.zip`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#2B2B2B] hover:bg-black rounded-xl px-4 py-2.5 mt-2"
                   data-testid="candidate-documents-download-zip-button">
                  <FolderArchive className="h-4 w-4" /> {t("application.downloadAllZip")}
                </a>
              )}
            </div>
          ) : user?.role === "producer" ? (
            <Dialog>
              <DialogTrigger asChild>
                <Button className="h-12 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold gap-2" data-testid="candidate-unlock-button">
                  <Lock className="h-4 w-4" /> {t("application.unlockCandidate")} ({t("credits.unlockCost")})
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl">
                <DialogHeader>
                  <DialogTitle>{t("application.unlockConfirmTitle")}</DialogTitle>
                  <DialogDescription>{t("application.unlockConfirmMessage")}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button onClick={handleUnlock} disabled={unlocking} className="bg-[#4ba3b7] hover:bg-[#3D8A9A] rounded-xl" data-testid="candidate-unlock-confirm-button">
                    {unlocking ? t("common.loading") : t("common.confirm")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : (
            <p className="text-sm text-[#2B2B2B]/40 flex items-center gap-2"><Lock className="h-4 w-4" /> {t("application.contactRevealed")}</p>
          )}
        </div>
      </div>
    </div>
  );
}
